import { Combobox } from '@/global/components/Combobox'
import React, { useEffect, useMemo } from 'react'
import { centroCusto, centroCusto_v2 } from 'service/api'
import { useStore } from 'store'
import { mapRenderCentros } from 'templates/Cadastro/CentroCusto/mapArvore'
import { SelectProps } from './types'
import { deveExibirSelect } from './visibilidade'

interface IProps extends SelectProps {
    children?: never
    /** Não filtrar por empresa e sim por cliente */
    listAll?: boolean
}

type CentroCustoOption = {
    id: number
    codigo: string
    nomeCentroCusto: string
    space: number
}

/** O código entra no rótulo, então a busca da lista já cobre código e nome. */
function rotuloDe(option: CentroCustoOption) {
    if (option.id === -1) return option.nomeCentroCusto
    return `${option.codigo} - ${option.nomeCentroCusto}`
}

export const SelectCentroCusto: React.FC<IProps> = ({
    size = 'small',
    variant = 'filled',
    width = 250,
    listAll = false,
    disabled,
    helperText,
    className,
    hideWhenSingle = false,
}) => {
    const empresaId = useStore(state => state.companyId)
    const costCenterId = useStore(state => state.costCenterId)
    const setCostCenterId = useStore(state => state.setCostCenterId)

    const {
        data: filteredData = [],
        isFetched: isFilteredFetched,
        isFetching: isFilteredFetching,
    } = centroCusto.useFetch({
        queryParams: { empresaId },
        enabled: !!empresaId && !listAll,
    })
    const {
        data: allData = [],
        isFetched: isAllFetched,
        isFetching: isAllFetching,
    } = centroCusto_v2.useFetch({ enabled: listAll })

    const data = listAll ? allData : filteredData,
        isFetched = listAll ? isAllFetched : isFilteredFetched,
        isFetching = listAll ? isAllFetching : isFilteredFetching

    const options = useMemo<CentroCustoOption[]>(() => {
        const centros = mapRenderCentros(data).map(node => ({
            id: node.id,
            codigo: node.codigo,
            nomeCentroCusto: node.nomeCentroCusto,
            space: node.space,
        }))

        return [
            {
                id: -1,
                codigo: '',
                nomeCentroCusto: 'Não filtrar',
                space: 0,
            },
            ...centros,
        ]
    }, [data])

    useEffect(() => {
        if (
            costCenterId > 0 &&
            isFetched &&
            !data.map(it => it.id).includes(costCenterId)
        )
            setCostCenterId(-1)
    }, [empresaId, isFetched])

    const selectedCentroCusto =
        options.find(it => it.id === costCenterId) ?? options[0] ?? null

    return deveExibirSelect(data.length, hideWhenSingle, isFetched) ? (
        <div style={{ width }} className={className}>
            <Combobox<CentroCustoOption>
                label="Centro de custo"
                size={size}
                variant={variant === 'outlined' ? 'outlined' : 'filled'}
                placeholder="Selecione um centro de custo"
                options={options}
                value={selectedCentroCusto}
                onChange={value => setCostCenterId(value?.id ?? -1)}
                getOptionLabel={rotuloDe}
                getOptionKey={option => String(option.id)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={option => (
                    <span
                        title={option.nomeCentroCusto}
                        className="block truncate"
                        style={{ paddingLeft: `${option.space * 24}px` }}
                    >
                        {rotuloDe(option)}
                    </span>
                )}
                loading={isFetching}
                noOptionsText="Não há centros de custo cadastrados"
                helperText={helperText}
                disabled={disabled}
            />
        </div>
    ) : null
}
